import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import Payment from '@mui/icons-material/Payment'
import Cancel from '@mui/icons-material/Cancel'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { toast } from 'react-toastify'
import {
  cancelRechargeRequest,
  createRechargeRequest,
  getPaymentLinks,
  getRechargeModel,
} from 'src/business/services/APIs/CompanyCredits/companyCreditsApi'
import { openWalletRazorpay } from './walletRazorpay'

const baseUrl = () => `${window.location.origin}${window.location.pathname}`

/** Statuses that allow cancel (doc: cancel before payment completed) */
const CANCELLABLE_STATUSES = ['requested', 'payment_initiated', 'payment_pending']

export default function WalletRecharge({ onRefresh }) {
  const [rechargeModel, setRechargeModel] = useState(null)
  const [modelLoading, setModelLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [links, setLinks] = useState([])
  const [linksLoading, setLinksLoading] = useState(true)
  const [form, setForm] = useState({
    pricingOptionId: '',
    paymentGateway: '',
    paymentMethod: 'upi',
    note: '',
    source: 'business-portal',
    notifyViaEmail: false,
    notifyViaWhatsapp: false,
    customEmails: '',
    customWhatsappNumbers: '',
    successUrl: baseUrl(),
    cancelUrl: baseUrl(),
  })

  const options = rechargeModel?.options ?? []
  const selectedOption = options.find((o) => (o.optionId ?? o.id) === form.pricingOptionId)
  const allowedGateways = selectedOption?.paymentGateways ?? []

  const fetchModel = useCallback(async () => {
    setModelLoading(true)
    try {
      const res = await getRechargeModel()
      if (res?.status === 200 && res?.data) {
        const model = res.data?.rechargePricingModel ?? res.data
        setRechargeModel(model)
        const opts = model?.options ?? []
        const defaultId = model?.defaultOptionId ?? opts[0]?.optionId ?? opts[0]?.id
        const gateways = opts.find((o) => (o.optionId ?? o.id) === defaultId)?.paymentGateways ?? []
        if (defaultId) {
          setForm((f) => ({
            ...f,
            pricingOptionId: defaultId,
            paymentGateway: gateways[0] ?? f.paymentGateway,
          }))
        }
      }
    } finally {
      setModelLoading(false)
    }
  }, [])

  const fetchLinks = useCallback(async () => {
    setLinksLoading(true)
    try {
      const res = await getPaymentLinks({ page: 0, size: 50 })
      if (res?.status === 200 && res?.data) {
        const list = Array.isArray(res.data) ? res.data : res.data?.content ?? res.data?.data ?? []
        setLinks(Array.isArray(list) ? list : [])
      }
    } finally {
      setLinksLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchModel()
  }, [fetchModel])

  useEffect(() => {
    fetchLinks()
  }, [fetchLinks])

  useEffect(() => {
    if (selectedOption && allowedGateways.length > 0 && !allowedGateways.includes(form.paymentGateway)) {
      setForm((f) => ({ ...f, paymentGateway: allowedGateways[0] }))
    }
  }, [selectedOption, allowedGateways, form.paymentGateway])

  const handleCreate = async (e) => {
    e?.preventDefault()
    if (!form.pricingOptionId) {
      toast.error('Please select a recharge option')
      return
    }
    if (!rechargeModel?.approved) {
      toast.error('Recharge model is not approved. Contact your administrator.')
      return
    }
    setCreating(true)
    try {
      const rechargeRequest = {
        pricingOptionId: form.pricingOptionId,
        paymentGateway: form.paymentGateway || undefined,
        paymentMethod: form.paymentMethod || undefined,
        note: form.note || undefined,
        source: 'business-portal',
        notifyViaEmail: !!form.notifyViaEmail,
        notifyViaWhatsapp: !!form.notifyViaWhatsapp,
        successUrl: form.successUrl || baseUrl(),
        cancelUrl: form.cancelUrl || baseUrl(),
      }
      if (form.customEmails?.trim()) {
        rechargeRequest.customEmails = form.customEmails.trim().split(/[\s,]+/).filter(Boolean)
      }
      if (form.customWhatsappNumbers?.trim()) {
        rechargeRequest.customWhatsappNumbers = form.customWhatsappNumbers.trim().split(/[\s,]+/).filter(Boolean)
      }
      const res = await createRechargeRequest({ rechargeRequest })
      if (res?.status === 200) {
        toast.success('Recharge request created')
        setForm((f) => ({ ...f, note: '' }))
        fetchLinks()
        onRefresh?.()
      } else {
        const msg = res?.data?.message ?? res?.response?.data?.message ?? 'Failed to create request'
        toast.error(typeof msg === 'string' ? msg : 'Failed to create recharge request')
      }
    } catch (err) {
      toast.error('Failed to create recharge request')
    } finally {
      setCreating(false)
    }
  }

  const handlePay = (row) => {
    if (row.payable === false) return
    const type = row.paymentActionType
    const payload = row.paymentActionPayload || {}
    if (type === 'open-url' && payload.url) {
      window.open(payload.url, '_blank')
      return
    }
    if (type === 'razorpay-checkout') {
      openWalletRazorpay(payload, () => {
        fetchLinks()
        onRefresh?.()
      })
    }
  }

  const handleCancel = async (row) => {
    const id = row.rechargeRequestId
    if (!id) return
    try {
      const res = await cancelRechargeRequest({ rechargeRequestId: id, reason: 'Cancelled from portal' })
      if (res?.status === 200) {
        toast.success('Request cancelled')
        fetchLinks()
        onRefresh?.()
      } else {
        toast.error(res?.data?.message ?? res?.response?.data?.message ?? 'Could not cancel')
      }
    } catch (err) {
      toast.error('Could not cancel request')
    }
  }

  const copyToClipboard = (value, label = 'Value') => {
    if (!value) return
    navigator.clipboard.writeText(String(value)).then(() => toast.success(`${label} copied to clipboard`))
  }

  if (modelLoading) {
    return <LoadingSpinner bgColor="#F5F5F599" dark />
  }

  const modelApproved = rechargeModel?.approved
  const hasOptions = options.filter((o) => o.enabled !== false).length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            Create recharge request
          </Typography>
          {!modelApproved || !hasOptions ? (
            <Typography color="text.secondary">
              No recharge options available. Your administrator must assign an approved pricing model.
            </Typography>
          ) : (
            <Box
              component="form"
              onSubmit={handleCreate}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}
            >
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="recharge-option-label">Recharge option</InputLabel>
                <Select
                  labelId="recharge-option-label"
                  value={form.pricingOptionId}
                  onChange={(e) => setForm((f) => ({ ...f, pricingOptionId: e.target.value }))}
                  displayEmpty
                  required
                  label="Recharge option"
                >
                  <MenuItem value="">Select option…</MenuItem>
                  {options.filter((o) => o.enabled !== false).map((opt) => (
                    <MenuItem key={opt.optionId ?? opt.id} value={opt.optionId ?? opt.id}>
                      {opt.title ?? opt.optionId} — {opt.currencyCode ?? 'INR'} {opt.amount != null ? opt.amount : ''} ({opt.credits ?? 0} credits)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {allowedGateways.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="gateway-label">Payment gateway</InputLabel>
                  <Select
                    labelId="gateway-label"
                    value={form.paymentGateway}
                    onChange={(e) => setForm((f) => ({ ...f, paymentGateway: e.target.value }))}
                    displayEmpty
                    label="Payment gateway"
                  >
                    <MenuItem value="">Default</MenuItem>
                    {allowedGateways.map((gw) => (
                      <MenuItem key={gw} value={gw}>{gw}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                size="small"
                label="Note"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="e.g. Q1 wallet topup"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.notifyViaEmail}
                    onChange={(e) => setForm((f) => ({ ...f, notifyViaEmail: e.target.checked }))}
                  />
                }
                label="Notify via email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.notifyViaWhatsapp}
                    onChange={(e) => setForm((f) => ({ ...f, notifyViaWhatsapp: e.target.checked }))}
                  />
                }
                label="Notify via WhatsApp"
              />
              <TextField
                size="small"
                label="Custom emails (comma-separated)"
                value={form.customEmails}
                onChange={(e) => setForm((f) => ({ ...f, customEmails: e.target.value }))}
                placeholder="finance@acme.com, owner@acme.com"
              />
              <TextField
                size="small"
                label="Custom WhatsApp numbers (comma-separated)"
                value={form.customWhatsappNumbers}
                onChange={(e) => setForm((f) => ({ ...f, customWhatsappNumbers: e.target.value }))}
                placeholder="+919876543210"
              />
              <Button type="submit" variant="contained" disabled={creating}>
                {creating ? 'Creating…' : 'Create & get payment link'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            Payment links
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            After paying, refresh or re-open this tab to see updated balance and status.
          </Typography>
          {linksLoading ? (
            <LoadingSpinner bgColor="#F5F5F599" dark />
          ) : links.length === 0 ? (
            <Typography color="text.secondary">No payment links yet. Create a recharge request above.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Status</TableCell>
                    <TableCell>Option / Credits / Amount</TableCell>
                    <TableCell>Gateway</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {links.map((row) => {
                    const canCancel = CANCELLABLE_STATUSES.includes(row.status)
                    return (
                      <TableRow key={row.rechargeRequestId || row.paymentOrderId || row.paymentReferenceId || row.id}>
                        <TableCell>{row.status || '—'}</TableCell>
                        <TableCell>
                          {row.pricingOptionTitle || row.pricingOptionId || '—'} / {row.requestedCredits ?? '—'} / {row.currencyCode ?? ''} {row.requestedAmount ?? row.paidAmount ?? '—'}
                        </TableCell>
                        <TableCell>{row.paymentGateway || '—'}</TableCell>
                        <TableCell align="right">
                          {row.paymentCopyValue != null && (
                            <Button
                              size="small"
                              startIcon={<ContentCopy />}
                              onClick={() => copyToClipboard(row.paymentCopyValue, row.paymentCopyType || 'Link')}
                            >
                              Copy {row.paymentCopyType ? `(${row.paymentCopyType})` : ''}
                            </Button>
                          )}
                          {row.payable === true && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Payment />}
                              onClick={() => handlePay(row)}
                              sx={{ ml: 1 }}
                            >
                              Pay
                            </Button>
                          )}
                          {row.payable === false && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              Pay disabled
                            </Typography>
                          )}
                          {canCancel && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => handleCancel(row)}
                              sx={{ ml: 1 }}
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
